import type { CSSProperties } from 'react';

/** An sRGB colour as three 8-bit channels. */
export type Rgb = [number, number, number];

/**
 * Colour tokens of one booth. `color` is the brand (CI) colour, `ink` the text colour on it,
 * `tint` a light surface of the brand colour and `accent` the brand colour as it may appear on
 * white (active tab, badges). `isFallback` is true when the exhibitor has no valid CI colour.
 */
export interface BoothPalette {
  color: string;
  ink: string;
  tint: string;
  accent: string;
  isFallback: boolean;
}

/** Naskor blue, used when the brand has no valid CI colour. */
const FALLBACK_COLOR = '#254a91';
const INK_DARK = '#111418';
const INK_LIGHT = '#ffffff';
const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
/** Share of the brand colour in the tint; the rest is white. */
const TINT_SHARE = 0.12;
/** WCAG 2.1 minimum contrast for UI components and large text (SC 1.4.11). */
const ACCENT_MIN_CONTRAST = 3;
/** Black is mixed into the accent in steps of 5 %. */
const ACCENT_DARKEN_STEPS = 20;

/**
 * Parses `#rgb`, `#rrggbb`, `rgb` or `rrggbb` (case-insensitive). Returns null for anything else.
 */
export function parseHex(input?: string | null): Rgb | null {
  if (!input) {
    return null;
  }
  const hex = input.trim().replace(/^#/, '');
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) {
    return null;
  }
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : hex;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/**
 * Relative luminance after WCAG 2.1:
 * L = 0.2126 R + 0.7152 G + 0.0722 B, with each channel c = v / 255 linearised as
 * c / 12.92 for c <= 0.04045, else ((c + 0.055) / 1.055) ^ 2.4.
 * (WCAG 2.1 prints the threshold as 0.03928; for 8-bit channels both give the same result.)
 */
export function relativeLuminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map((value) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Contrast ratio after WCAG 2.1: (L1 + 0.05) / (L2 + 0.05), where L1 is the lighter
 * of the two luminances. Ranges from 1 (equal) to 21 (black on white).
 */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);

  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Builds the palette of a booth from its CI colour. Without a valid colour the palette is
 * based on Naskor blue.
 * - ink: #111418 or #ffffff, whichever has the higher contrast to the colour.
 * - tint: 12 % colour + 88 % white, per channel.
 * - accent: the colour if its contrast to white is at least 3.0; otherwise the colour mixed
 *   with black in 5 % steps until it is (yellow turns into a dark gold, dark blue stays).
 */
export function boothPalette(ciColor?: string | null): BoothPalette {
  const parsed = parseHex(ciColor);
  const rgb = parsed ?? (parseHex(FALLBACK_COLOR) as Rgb);

  const inkDark = parseHex(INK_DARK) as Rgb;
  const ink =
    contrastRatio(rgb, inkDark) >= contrastRatio(rgb, WHITE)
      ? INK_DARK
      : INK_LIGHT;

  let accent = rgb;
  for (
    let step = 1;
    step <= ACCENT_DARKEN_STEPS &&
    contrastRatio(accent, WHITE) < ACCENT_MIN_CONTRAST;
    step++
  ) {
    accent = mix(BLACK, rgb, step / ACCENT_DARKEN_STEPS);
  }

  return {
    color: toHex(rgb),
    ink,
    tint: toHex(mix(rgb, WHITE, TINT_SHARE)),
    accent: toHex(accent),
    isFallback: parsed === null,
  };
}

/** The palette as CSS custom properties for the `style` of the booth sheet. */
export function boothCssVars(p: BoothPalette): CSSProperties {
  return {
    '--booth-color': p.color,
    '--booth-ink': p.ink,
    '--booth-tint': p.tint,
    '--booth-accent': p.accent,
  } as CSSProperties;
}

/** Per channel: share * a + (1 - share) * b. */
function mix(a: Rgb, b: Rgb, share: number): Rgb {
  return [0, 1, 2].map((i) =>
    Math.round(share * a[i] + (1 - share) * b[i])
  ) as Rgb;
}

function toHex(rgb: Rgb): string {
  return (
    '#' +
    rgb
      .map((value) =>
        Math.min(255, Math.max(0, value)).toString(16).padStart(2, '0')
      )
      .join('')
  );
}
